export const cookies = {
  // Cookie Banner
  banner: {
    title: 'We Value Your Privacy',
    description: 'We use cookies to enhance your browsing experience, analyze our traffic, and improve our services. By clicking "Accept All", you consent to our use of cookies.',
    learnMore: 'Learn more',
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    customize: 'Customize',
    essentialShort: 'Essential',
    analyticsShort: 'Analytics',
    footerNote: 'You can change your preferences at any time from the footer.',
  },

  // Cookie Settings Modal
  settings: {
    title: 'Cookie Preferences',
    description: 'We use different types of cookies to optimize your experience on our website. Choose which cookies you want to allow.',
    learnMore: 'Read our Cookie Policy',
    close: 'Close',
    cookiesUsed: 'Cookies used',
    acceptAll: 'Accept All',
    rejectAll: 'Reject Only Essential',
    save: 'Save Preferences',
    cancel: 'Cancel',
    footerNote: 'Your privacy is important to us. We are committed to protecting your data.',

    // Essential Cookies
    essential: {
      title: 'Essential Cookies',
      description: 'These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt-out of these cookies.',
      cookies: 'session_id, auth_token, language_preference',
      required: '(Required)',
    },

    // Analytics Cookies
    analytics: {
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services.',
      cookies: '_ga, _ga_*, _gid (Google Analytics 4)',
    },

    // Marketing Cookies (Future use)
    marketing: {
      title: 'Marketing Cookies',
      description: 'These cookies track your online activity to help advertisers deliver more relevant advertising. We do not currently use marketing cookies.',
      cookies: 'None at this time',
    },
  },

  // Cookie Policy Page
  policy: {
    title: 'Cookie Policy',
    lastUpdated: 'January 25, 2026',
    lastUpdatedLabel: 'Last Updated',

    nav: {
      title: 'Table of Contents',
      introduction: 'Introduction',
      categories: 'Cookie Categories',
      cookiesWeUse: 'Cookies We Use',
      googleAnalytics: 'Google Analytics 4',
      yourChoices: 'Your Choices',
      contact: 'Contact Us',
    },

    sections: {
      introduction: {
        title: '1. Introduction',
        content: `
          <p>This Cookie Policy explains how TerminTeiler ("we", "us", or "our") uses cookies and similar technologies on our website and associated services.</p>
          
          <p>Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They help us remember your login status and preferences, understand how you use our website, improve your user experience, and analyze website traffic and performance.</p>
          
          <p>We are committed to transparency and your privacy. This policy explains what cookies we use, why we use them, and how you can control them.</p>
        `,
      },

      categories: {
        title: '2. Types of Cookies',
        content: `
          <p>We categorize cookies based on their purpose and allow you to control which cookies you accept:</p>
          
          <h3>2.1 Essential Cookies (Always Active)</h3>
          <p>These cookies are strictly necessary for the website to function. Without these cookies, services you have requested cannot be provided. They enable user authentication, session management, language preferences, and basic functionality.</p>
          <p><strong>Legal Basis:</strong> Legitimate interest (GDPR Art. 6(1)(f)) - necessary for providing the service you requested.</p>
          
          <h3>2.2 Analytics Cookies (Requires Consent)</h3>
          <p>These cookies collect information about how you use our website. All information is aggregated and anonymized. They track pages visited, time spent on pages, navigation patterns, and device and browser information (anonymized).</p>
          <p><strong>Legal Basis:</strong> Consent (GDPR Art. 6(1)(a)) - you explicitly agree to their use.</p>
          
          <h3>2.3 Marketing Cookies (Future Use)</h3>
          <p>We do not currently use marketing or advertising cookies. If we decide to use them in the future, we will update this policy and request your explicit consent.</p>
        `,
      },

      cookiesWeUse: {
        title: '3. Detailed Cookie Information',
        content: `
          <p>Below is a complete list of cookies used on our website:</p>
          
          <h3>3.1 Essential Cookies</h3>
          <table>
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>session_id</strong></td>
                <td>Maintains your session while you navigate the website</td>
                <td>Session (deleted when browser closes)</td>
                <td>First-party</td>
              </tr>
              <tr>
                <td><strong>auth_token</strong></td>
                <td>Keeps you logged in between visits</td>
                <td>30 days or until logout</td>
                <td>First-party</td>
              </tr>
              <tr>
                <td><strong>language_preference</strong></td>
                <td>Remembers your language choice (DE/EN)</td>
                <td>1 year</td>
                <td>First-party</td>
              </tr>
              <tr>
                <td><strong>cookieConsent</strong></td>
                <td>Stores your cookie preferences</td>
                <td>6 months</td>
                <td>First-party</td>
              </tr>
            </tbody>
          </table>
          
          <h3>3.2 Analytics Cookies (Google Analytics 4)</h3>
          <p><strong>Only set if you consent to Analytics cookies</strong></p>
          
          <table>
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>_ga</strong></td>
                <td>Distinguishes unique users</td>
                <td>2 years</td>
                <td>Third-party (Google)</td>
              </tr>
              <tr>
                <td><strong>_ga_*</strong></td>
                <td>Persists session state</td>
                <td>2 years</td>
                <td>Third-party (Google)</td>
              </tr>
              <tr>
                <td><strong>_gid</strong></td>
                <td>Distinguishes users</td>
                <td>24 hours</td>
                <td>Third-party (Google)</td>
              </tr>
            </tbody>
          </table>
        `,
      },

      googleAnalytics: {
        title: '4. Google Analytics 4',
        content: `
          <p>We use Google Analytics 4 (GA4) to understand how our website is used and to improve our services. GA4 is only activated if you provide explicit consent for Analytics cookies.</p>
          
          <h3>What Data Does GA4 Collect?</h3>
          <ul>
            <li><strong>IP Address:</strong> Anonymized automatically (last octet removed)</li>
            <li><strong>Browser & Device Information:</strong> Browser type, operating system, screen size</li>
            <li><strong>Pages Visited:</strong> URLs and page titles you view</li>
            <li><strong>Referral Source:</strong> How you arrived at our website</li>
            <li><strong>Session Duration:</strong> Time spent on our website</li>
            <li><strong>Geographic Location:</strong> Country and city (approximate, based on anonymized IP)</li>
          </ul>
          
          <h3>Privacy Safeguards</h3>
          <p>We have configured Google Analytics 4 with the following privacy-first settings:</p>
          <ul>
            <li><strong>IP Anonymization:</strong> Enabled (required by GDPR)</li>
            <li><strong>Google Signals:</strong> Disabled (no cross-device tracking)</li>
            <li><strong>Ad Personalization:</strong> Disabled (no advertising use)</li>
            <li><strong>Data Retention:</strong> 14 months maximum</li>
            <li><strong>Data Sharing:</strong> Disabled for Google products and services</li>
            <li><strong>Data Processing Agreement:</strong> Signed with Google, including Standard Contractual Clauses for EU-US data transfers</li>
          </ul>
          
          <p><strong>Data Storage:</strong> Google Analytics data may be processed in the European Union (primary location) and United States (with Standard Contractual Clauses).</p>
          <p>More information: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></p>
        `,
      },

      yourChoices: {
        title: '5. How to Control Cookies',
        content: `
          <p>You have full control over which cookies are set on your device. Here's how to manage your cookie preferences:</p>
          
          <h3>5.1 Using Our Cookie Settings</h3>
          <p>The easiest way to manage cookies is through our Cookie Settings panel:</p>
          <ol>
            <li>Click "Cookie Settings" in the website footer</li>
            <li>Toggle individual cookie categories on/off</li>
            <li>Click "Save Preferences"</li>
          </ol>
          <p>Your preferences will be saved for 6 months.</p>
          
          <h3>5.2 Browser Settings</h3>
          <p>You can also control cookies through your browser settings. Most browsers allow you to block cookies, delete stored cookies, or get notified about new cookies.</p>
          
          <h3>5.3 Opt-Out of Google Analytics</h3>
          <p>You can opt-out of Google Analytics specifically by:</p>
          <ul>
            <li>Using our Cookie Settings to disable Analytics cookies</li>
            <li>Installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a></li>
          </ul>
          
          <p><strong>Note:</strong> Blocking essential cookies may prevent you from using certain features of our website, such as logging in.</p>
        `,
      },

      contact: {
        title: '6. Questions About Cookies?',
        content: `
          <p>If you have any questions about how we use cookies, please contact us:</p>
          
          <p><strong>TerminTeiler</strong><br/>
          Email: support@terminteiler.com<br/>
          Subject: "Cookie Policy Inquiry"<br/>
          Location: Siegen, Germany</p>
          
          <p>For information about your rights under GDPR regarding cookies and data collected through cookies, please see our <a href="/privacy-policy">Privacy Policy</a>.</p>
          
          <p>If you are not satisfied with how we handle cookies, you have the right to lodge a complaint with the supervisory authority. Contact details are available in our <a href="/privacy-policy">Privacy Policy</a>.</p>
          
          <hr style="margin: 2rem 0; border: none; border-top: 1px solid #e0e0e0;"/>
          
          <p><strong>Thank you for trusting TerminTeiler with your privacy!</strong></p>
          <p>We are committed to protecting your data and being transparent about our use of cookies.</p>
        `,
      },
    },
  },
};
